// Concert / worship stage lights: colored beams that fan down from the top
// of the screen and sweep back and forth, blended additively so overlaps
// brighten like real spotlights. Nothing like the warm top glow - this reads
// as a party light show.
const beamColors = [
  '#ff2d95', // magenta
  '#00e5ff', // cyan
  '#845ec2', // purple
  '#ffd166', // gold
  '#3a86ff', // blue
  '#ff6b6b' // coral
]

type Beam = {
  /** origin x as a fraction of the viewport width */
  originFraction: number
  /** centre angle in radians (Math.PI/2 = straight down) */
  baseAngle: number
  /** how far the beam swings to each side, in radians */
  sweep: number
  /** angular speed of the swing */
  speed: number
  /** phase offset so beams don't move in lockstep */
  phase: number
  /** half of the beam's opening angle, in radians */
  halfWidth: number
  color: string
}

function withAlpha(hex: string, alpha: number): string {
  const value = hex.replace('#', '')
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function playLights(options: { durationMs?: number }) {
  const { durationMs = 4000 } = options
  // The beams sweep, which is real motion - honour the reduced-motion
  // preference by keeping them still and letting only the opacity breathe.
  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches

  const canvas = document.createElement('canvas')
  canvas.setAttribute('aria-hidden', 'true')
  canvas.style.cssText =
    'position:fixed;inset:0;z-index:9999;pointer-events:none;width:100%;height:100%'
  const context = canvas.getContext('2d')
  if (!context) return

  const dpr = window.devicePixelRatio || 1
  const width = window.innerWidth
  const height = window.innerHeight
  canvas.width = width * dpr
  canvas.height = height * dpr
  context.scale(dpr, dpr)
  document.body.appendChild(canvas)

  // beams reach past the far corner so they always cover the screen
  const beamLength = Math.hypot(width, height) * 1.2
  const straightDown = Math.PI / 2

  const beams: Beam[] = beamColors.map((color, index) => {
    // spread the origins evenly across the top edge
    const originFraction = (index + 0.5) / beamColors.length
    // fan the rest angles outwards from the centre for a stage-truss look
    const spread = (originFraction - 0.5) * 0.9
    return {
      originFraction,
      baseAngle: straightDown + spread,
      sweep: 0.35 + Math.random() * 0.25,
      speed: 0.0016 + Math.random() * 0.0012,
      phase: Math.random() * Math.PI * 2,
      halfWidth: 0.05 + Math.random() * 0.02,
      color
    }
  })

  const startedAt = performance.now()
  const fadeMs = 600

  const drawBeam = (beam: Beam, angle: number) => {
    const originX = beam.originFraction * width
    const originY = -30
    const left = angle - beam.halfWidth
    const right = angle + beam.halfWidth
    const axisX = originX + Math.cos(angle) * beamLength
    const axisY = originY + Math.sin(angle) * beamLength

    const gradient = context.createLinearGradient(
      originX,
      originY,
      axisX,
      axisY
    )
    gradient.addColorStop(0, withAlpha(beam.color, 0.55))
    gradient.addColorStop(0.5, withAlpha(beam.color, 0.2))
    gradient.addColorStop(1, withAlpha(beam.color, 0))

    context.fillStyle = gradient
    context.beginPath()
    context.moveTo(originX, originY)
    context.lineTo(originX + Math.cos(left) * beamLength, originY + Math.sin(left) * beamLength)
    context.lineTo(originX + Math.cos(right) * beamLength, originY + Math.sin(right) * beamLength)
    context.closePath()
    context.fill()
  }

  const frame = (now: number) => {
    const elapsed = now - startedAt
    if (elapsed >= durationMs) {
      canvas.remove()
      return
    }

    const envelope =
      elapsed < fadeMs
        ? elapsed / fadeMs
        : elapsed > durationMs - fadeMs
          ? (durationMs - elapsed) / fadeMs
          : 1

    context.clearRect(0, 0, width, height)
    context.globalCompositeOperation = 'lighter'
    context.globalAlpha = envelope

    for (const beam of beams) {
      const angle = reduceMotion
        ? beam.baseAngle
        : beam.baseAngle + Math.sin(now * beam.speed + beam.phase) * beam.sweep
      drawBeam(beam, angle)
    }

    requestAnimationFrame(frame)
  }
  requestAnimationFrame(frame)

  // rAF pauses in hidden tabs - make sure the canvas never lingers
  setTimeout(() => canvas.remove(), durationMs + 4000)
}
